import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Avatar,
  List,
  ListItem,
  ListIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Button,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface Leader {
  name: string;
  role: string;
  organisation: string;
  relevance: string;
  keyPositions: string[];
}

const leaders: Leader[] = [
  {
    name: "Koichi Hagiuda",
    role: "Former METI Minister",
    organisation: "Japanese Government",
    relevance: "Key architect of Japan's energy security strategy",
    keyPositions: [
      "Designed Japan's post-Fukushima energy diversification plan",
      "Championed strategic petroleum reserve expansion",
      "Led negotiations for US crude import agreements",
    ],
  },
  {
    name: "METI",
    role: "Ministry of Economy, Trade and Industry",
    organisation: "Japanese Government",
    relevance: "Controls Japan's Strategic Petroleum Reserve and supply strategy",
    keyPositions: [
      "Manages Japan's 240-day strategic petroleum reserve",
      "Coordinates emergency fuel allocation during crises",
      "Sets energy import policy and trade agreements",
    ],
  },
  {
    name: "JOGMEC",
    role: "Japan Oil, Gas and Metals National Corporation",
    organisation: "State Corporation",
    relevance: "State body managing reserves and foreign energy investments",
    keyPositions: [
      "Manages physical storage of strategic petroleum reserves",
      "Invests in overseas oil and gas exploration",
      "Provides geological survey and technical support",
    ],
  },
  {
    name: "Koji Murofushi",
    role: "JXTG/Eneos Leadership",
    organisation: "Eneos Holdings",
    relevance: "Japan's largest refiner, dominates domestic supply",
    keyPositions: [
      "Operates ~50% of Japan's refining capacity",
      "Controls supply chain from import to retail",
      "Key relationship with Saudi Aramco and UAE suppliers",
    ],
  },
  {
    name: "Toichi Tsunoda",
    role: "Senior Analyst",
    organisation: "IEEJ (Institute of Energy Economics Japan)",
    relevance: "Key analyst/advisor to government on energy policy",
    keyPositions: [
      "Advises government on energy security scenarios",
      "Publishes influential energy outlook reports",
      "Models impact of supply disruptions on Japan's economy",
    ],
  },
];

const JapanDecisionMakers: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Japan's Decision Makers</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Japan's Key Energy Decision Makers
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            The people and institutions controlling Japan's energy strategy and emergency response
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {leaders.map((leader) => (
              <Box
                key={leader.name}
                p={6}
                bg={surfaceBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderLight}
                _hover={{
                  borderColor: "#dc2626",
                  shadow: "md",
                  transition: "all 0.2s",
                }}
                transition="all 0.2s"
              >
                <VStack align="start" spacing={3}>
                  <Avatar name={leader.name} size="md" bg="#dc2626" color="white" />
                  <Heading size="sm" color={textPrimary}>
                    {leader.name}
                  </Heading>
                  <Text fontSize="xs" color={textSecondary}>
                    {leader.role} -- {leader.organisation}
                  </Text>
                  <Badge colorScheme="red" fontSize="2xs">
                    {leader.relevance}
                  </Badge>
                  <List spacing={1} mt={2}>
                    {leader.keyPositions.map((pos, i) => (
                      <ListItem key={i} fontSize="sm" color={textPrimary}>
                        <ListIcon as={ChevronRightIcon} color="#dc2626" />
                        {pos}
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box textAlign="center" mt={4}>
            <Button
              as={RouterLink}
              to="/energy-research/relationship-map"
              colorScheme="red"
              variant="outline"
            >
              View Relationship Map (AU + Japan)
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default JapanDecisionMakers;
